"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { FiArrowDown, FiArrowUp, FiChevronRight, FiDownload, FiImage, FiList } from "react-icons/fi";
import { AnalyticsResultType } from "@/components/ai-chat/types";
import Pagination from "@/components/pagination";
import { AreaChart } from "@/components/ui/charts/area-chart";
import { BarChart } from "@/components/ui/charts/bar-chart";
import { BiaxialLineChart } from "@/components/ui/charts/biaxial-line-chart";
import { BoxPlotChart, type BoxPlotEntry } from "@/components/ui/charts/box-plot-chart";
import ChartWrapper from "@/components/ui/charts/chart-wrapper";
import { ComposedChart } from "@/components/ui/charts/composed-chart";
import { DonutChart } from "@/components/ui/charts/donut-chart";
import { FunnelChart } from "@/components/ui/charts/funnel-chart";
import { SankeyChart, type SankeyData } from "@/components/ui/charts/sankey-chart";
import { ScatterChart, type ScatterDataPoint } from "@/components/ui/charts/scatter-chart";
import { TreemapChart } from "@/components/ui/charts/treemap-chart";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { formatCurrencyDollars, formatDate, formatDateTime, formatMonthYear } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";
import { AnalyticsChartType } from "@/types/analytics-ai";
import type { AnalyticsResult, ChatArtifact, ChatArtifactChartConfig } from "@/types/analytics-ai";

/** Export a chart container's SVG as a PNG download. */
async function exportChartAsPng(containerEl: HTMLElement | null, filename = "chart.png") {
  if (!containerEl) return;
  const {
    toPng
  } = await import("html-to-image");
  const dataUrl = await toPng(containerEl, {
    pixelRatio: 2,
    backgroundColor: "#ffffff"
  });
  const link = document.createElement("a");
  link.download = filename;
  link.href = dataUrl;
  link.click();
}

/** Check if the column stores values in cents (needs /100 conversion) */
function isColumnInCents(columnHeader: string): boolean {
  return columnHeader.toLowerCase().includes("cent");
}

/**
 * Humanize a raw SQL column name for display in chart legends/labels.
 * e.g. "total_cents" -> "Total", "monthly_revenue" -> "Monthly Revenue"
 */
function humanizeColumnName(col: string): string {
  // Strip common suffixes that are implementation details
  let cleaned = col.replace(/_cents$/i, "").replace(/_dollars$/i, "").replace(/_amount$/i, "").replace(/_count$/i, " Count").replace(/_id$/i, "");
  // Convert snake_case to Title Case
  cleaned = cleaned.split("_").map(w => w.length > 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w).join(" ").trim();
  return cleaned || col;
}

/**
 * Format a raw value for use as an x-axis label.
 * Detects date/timestamp strings and formats them readably.
 */
function formatAxisLabel(val: unknown): string {
  if (val === null || val === undefined || val === "") return "None";
  const str = String(val);
  if (str.toLowerCase() === "null" || str.toLowerCase() === "none") return "None";
  // Detect date-only ("2025-04-01") or datetime ("2025-04-01 00:00:00" / "2025-04-01T12:30:00")
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
    const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(str);
    if (isDateOnly) {
      const day = Number(str.slice(8, 10));
      if (day === 1) {
        return formatDate(str, {
          month: "short",
          year: "numeric"
        });
      }
      return formatDate(str);
    }
    const d = new Date(str.replace(" ", "T"));
    if (!Number.isNaN(d.getTime())) {
      const isMidnight = d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0;
      if (isMidnight) {
        if (d.getDate() === 1) return formatMonthYear(d);
        return formatDate(d);
      }
      return formatDateTime(d);
    }
  }
  // snake_case or UPPER_SNAKE_CASE values → Title Case (e.g. PAYMENT_PLAN → Payment Plan)
  if (/^[A-Za-z][A-Za-z0-9]*(_[A-Za-z0-9]+)+$/.test(str)) {
    return str.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");
  }
  // ALL CAPS single word (e.g. MEMBERSHIP → Membership)
  if (/^[A-Z]{2,}$/.test(str)) {
    return str.charAt(0) + str.slice(1).toLowerCase();
  }
  return String(val);
}
function formatCell(cell: unknown, cellIdx: number, columnHeader: string, columnTypes?: Record<string, string>, currencyIndices?: number[], currencyColumns?: string[]): React.ReactNode {
  if (cell === null || cell === undefined) {
    return <span className="text-foreground-muted">—</span>;
  }
  if (typeof cell === "object") {
    return <span className="text-foreground-muted">
        {JSON.stringify(cell).slice(0, 100)}
      </span>;
  }
  const isCurrency = currencyIndices ? currencyIndices.includes(cellIdx) : currencyColumns ? currencyColumns.includes(columnHeader) : columnTypes?.[columnHeader] === "currency";
  if (isCurrency && (typeof cell === "number" || typeof cell === "string")) {
    return formatCurrencyDollars(cell);
  }
  // Format dates as human-readable (e.g. "2025-03-10 16:00:00" -> "Mar 10, 2025, 4:00 PM")
  const str = String(cell);
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    return formatDate(str);
  }
  if (/^\d{4}-\d{2}-\d{2}[\sT]\d{1,2}:\d{2}(:\d{2})?(\.\d+)?/.test(str) || cell instanceof Date) {
    // A string without an offset is a UTC timestamp straight from the
    // database; formatDateTime reads it as UTC, where `new Date` would read it
    // in the browser's zone.
    const value = cell instanceof Date ? cell : str.replace(" ", "T");
    if (!Number.isNaN(new Date(value).getTime())) {
      return formatDateTime(value);
    }
  }
  if (typeof cell === "number" && cell > 1e12) {
    const d = new Date(cell);
    if (!Number.isNaN(d.getTime())) return formatDateTime(d);
  }
  // Round floats to remove floating-point noise (e.g. 4076.530000000000 → 4076.53)
  if (typeof cell === "number" && !Number.isInteger(cell)) {
    return cell.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  }
  if (typeof cell === "number") {
    return cell.toLocaleString();
  }
  // UPPER_SNAKE_CASE or ALL_CAPS enum values → Title Case
  if (typeof cell === "string") {
    if (/^[A-Za-z][A-Za-z0-9]*(_[A-Za-z0-9]+)+$/.test(str)) {
      return str.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");
    }
    if (/^[A-Z]{2,}$/.test(str)) {
      return str.charAt(0) + str.slice(1).toLowerCase();
    }
  }
  return String(cell);
}

/**
 * Auto-pivot long-format data into wide format for charts.
 *
 * Detects when data has an index column, a string "category" column, and one
 * or more numeric value columns, then pivots so each unique category becomes
 * its own column. Works for any number of columns — not hardcoded to 3.
 *
 * Example: [Month, Service, Count] → [Month, "Service A", "Service B", ...]
 */
function autoPivotForChart(result: AnalyticsResult): AnalyticsResult {
  const {
    columns,
    rows
  } = result;
  if (columns.length < 3 || rows.length < 2) return result;

  // Find string-only columns (potential category) and numeric columns (values)
  // Skip column 0 (index)
  let categoryColIdx: number | null = null;
  const numericColIdxs: number[] = [];

  // Numeric includes actual numbers OR parseable numeric strings (Python may return strings)
  const isNumericOrNull = (v: unknown): boolean => {
    if (v === null || v === undefined) return true;
    if (typeof v === "number") return true;
    if (typeof v === "string") {
      const stripped = v.replace(/%$/, "").trim();
      return stripped !== "" && !Number.isNaN(Number(stripped));
    }
    return false;
  };
  for (let i = 1; i < columns.length; i++) {
    const allStrings = rows.every(r => typeof r[i] === "string" && r[i] !== null);
    const allNumericOrNull = rows.every(r => isNumericOrNull(r[i]));
    // A column is categorical only if it has strings that are NOT parseable as numbers
    const hasNonNumericString = rows.some(r => typeof r[i] === "string" && !isNumericOrNull(r[i]));
    if (allStrings && hasNonNumericString) {
      if (categoryColIdx === null) categoryColIdx = i;
    } else if (allNumericOrNull) {
      numericColIdxs.push(i);
    }
  }

  // Need exactly one category column and at least one numeric column
  if (categoryColIdx === null || numericColIdxs.length === 0) return result;

  // Need multiple unique categories and multiple index values
  const categories = Array.from(new Set(rows.map(r => String(r[categoryColIdx!]))));
  const indexValues = Array.from(new Set(rows.map(r => String(r[0]))));
  if (categories.length < 2 || indexValues.length < 2) return result;

  // Pivot: group by index, spread categories × values as columns
  const grouped = new Map<string, Record<string, unknown>>();
  for (const row of rows) {
    const idx = String(row[0]);
    const cat = String(row[categoryColIdx]);
    if (!grouped.has(idx)) {
      grouped.set(idx, {
        [columns[0]]: row[0]
      });
    }
    const entry = grouped.get(idx)!;
    for (const vi of numericColIdxs) {
      // If single value column, use category name directly as column header
      // If multiple value columns, combine: "Category - ValueName"
      const colName = numericColIdxs.length === 1 ? cat : `${cat} - ${columns[vi]}`;
      // Coerce numeric strings to numbers (Python may return "84.55" or "84.55%")
      const rawVal = row[vi];
      let val: number | null = null;
      if (rawVal !== null && rawVal !== undefined) {
        if (typeof rawVal === "number") {
          val = rawVal;
        } else if (typeof rawVal === "string") {
          const stripped = rawVal.replace(/%$/, "").trim();
          const parsed = Number(stripped);
          val = Number.isNaN(parsed) ? null : parsed;
        }
      }
      entry[colName] = val;
    }
  }

  // Build new column list
  const newCols = [columns[0]];
  for (const cat of categories) {
    for (const vi of numericColIdxs) {
      newCols.push(numericColIdxs.length === 1 ? cat : `${cat} - ${columns[vi]}`);
    }
  }
  const pivotedRows = Array.from(grouped.values()).map(obj => newCols.map(col => obj[col] as string | number | null ?? null));
  return {
    ...result,
    columns: newCols,
    rows: pivotedRows
  };
}

/** Transform rows/columns into chart data with humanized keys and formatted values */
function rowsToChartData(result: AnalyticsResult, xColumn: string, valueColumns: string[]): Record<string, string | number>[] {
  const {
    columns,
    rows
  } = result;
  return rows.map(row => {
    const obj: Record<string, string | number> = {};
    row.forEach((cell, i) => {
      const rawCol = columns[i] ?? `col_${i}`;
      if (rawCol !== xColumn && !valueColumns.includes(rawCol)) return;
      const displayCol = humanizeColumnName(rawCol);
      if (rawCol === xColumn) {
        // Index column: format dates/enums for readable x-axis labels
        obj[displayCol] = formatAxisLabel(cell);
      } else {
        // Value columns: coerce numeric strings to numbers so recharts can plot them.
        // Python post-processing may return values as strings (e.g. "84.55" or "84.55%").
        let value: string | number = cell as string | number;
        if (typeof value === "string") {
          const stripped = value.replace(/%$/, "").trim();
          const parsed = Number(stripped);
          if (!Number.isNaN(parsed) && stripped !== "") value = parsed;
        }
        if (typeof value === "number" && isColumnInCents(rawCol)) {
          value = value / 100;
        }
        obj[displayCol] = value;
      }
    });
    return obj;
  });
}

/** Returns true if the value looks like an ISO date/datetime string. */
function isISODateString(val: unknown): boolean {
  if (typeof val !== "string") return false;
  // Matches "2025-03-22" and "2025-03-22T..." formats
  return /^\d{4}-\d{2}-\d{2}/.test(val) && !Number.isNaN(new Date(val).getTime());
}

/**
 * Convert tabular analytics result to scatter data points.
 * Column mapping strategy:
 * - First two plottable columns (numeric OR ISO date string) → x, y
 * - Column named "category"/"group"/"series"/"type" → category grouping
 * - Column named "error_x"/"x_error" → x error bars
 * - Column named "error_y"/"y_error" → y error bars
 * - Column named "size"/"z"/"weight" → z dimension (bubble)
 */
function rowsToScatterData(result: AnalyticsResult): {
  points: ScatterDataPoint[];
  xLabel: string;
  yLabel: string;
  xColumn: string;
  yColumn: string;
  xIsDate: boolean;
  yIsDate: boolean;
} {
  const {
    columns,
    rows
  } = result;

  // Find column indices by role
  const lowerCols = columns.map(c => c.toLowerCase());
  const categoryIdx = lowerCols.findIndex(c => /^(category|group|series|type|name|label)$/.test(c));
  const errorXIdx = lowerCols.findIndex(c => /^(error_x|x_error|xerror|x_err)$/.test(c));
  const errorYIdx = lowerCols.findIndex(c => /^(error_y|y_error|yerror|y_err)$/.test(c));
  const sizeIdx = lowerCols.findIndex(c => /^(size|z|weight|bubble)$/.test(c));

  // Exclude special columns, find first two plottable columns (numeric or date) for x and y
  const specialIndices = new Set([categoryIdx, errorXIdx, errorYIdx, sizeIdx].filter(i => i >= 0));
  const plottableColIndices: number[] = [];
  const isDateCol: boolean[] = new Array(columns.length).fill(false);
  for (let i = 0; i < columns.length; i++) {
    const sample = rows.slice(0, 5);
    isDateCol[i] = sample.some(row => isISODateString(row[i]));
    if (specialIndices.has(i)) continue;
    const isPlottable = sample.some(row => {
      const val = row[i];
      return typeof val === "number" || typeof val === "string" && !Number.isNaN(Number(val)) && val.trim() !== "" || isISODateString(val);
    });
    if (isPlottable) {
      plottableColIndices.push(i);
    }
  }

  // Need at least two distinct columns — never reuse the same column for x and y
  const explicitX = columns.indexOf(result.x_column ?? "");
  const explicitY = columns.indexOf(result.y_columns?.[0] ?? "");
  const xIdx = explicitX >= 0 ? explicitX : plottableColIndices[0] ?? 0;
  const yIdx = explicitY >= 0 ? explicitY : plottableColIndices.find(i => i !== xIdx) ?? (columns.length > 1 ? xIdx === 0 ? 1 : 0 : xIdx);
  const toNum = (val: unknown, isDate: boolean): number => {
    if (isDate && typeof val === "string") {
      return new Date(val).getTime();
    }
    if (typeof val === "number") return val;
    if (typeof val === "string") {
      const n = Number(val);
      return Number.isNaN(n) ? 0 : n;
    }
    return 0;
  };
  const xIsDate = isDateCol[xIdx] ?? false;
  const yIsDate = isDateCol[yIdx] ?? false;
  const points: ScatterDataPoint[] = rows.map(row => {
    let x = toNum(row[xIdx], xIsDate);
    let y = toNum(row[yIdx], yIsDate);

    // Convert cents to dollars if applicable (only for non-date columns)
    if (!xIsDate && isColumnInCents(columns[xIdx] ?? "")) x = x / 100;
    if (!yIsDate && isColumnInCents(columns[yIdx] ?? "")) y = y / 100;
    const point: ScatterDataPoint = {
      x,
      y
    };
    if (categoryIdx >= 0 && row[categoryIdx] != null) {
      point.category = String(row[categoryIdx]);
    }
    if (errorXIdx >= 0 && row[errorXIdx] != null) {
      point.errorX = toNum(row[errorXIdx], false);
    }
    if (errorYIdx >= 0 && row[errorYIdx] != null) {
      point.errorY = toNum(row[errorYIdx], false);
    }
    if (sizeIdx >= 0 && row[sizeIdx] != null) {
      point.z = toNum(row[sizeIdx], false);
    }
    return point;
  });
  return {
    points,
    xLabel: humanizeColumnName(columns[xIdx] ?? "X"),
    yLabel: humanizeColumnName(columns[yIdx] ?? "Y"),
    xColumn: columns[xIdx] ?? "",
    yColumn: columns[yIdx] ?? "",
    xIsDate,
    yIsDate
  };
}

/**
 * Convert tabular analytics result to Sankey data.
 * Expects columns: [source, target, value] (any order — detected by name).
 * Falls back to column positions 0, 1, 2 if names don't match.
 */
function rowsToSankeyData(result: AnalyticsResult): SankeyData {
  const {
    columns,
    rows
  } = result;
  const lowerCols = columns.map(c => c.toLowerCase());
  const sourceIdx = lowerCols.findIndex(c => /^(source|from|origin|src)$/.test(c)) ?? lowerCols.findIndex(c => c.includes("source") || c.includes("from")) ?? 0;
  const targetIdx = lowerCols.findIndex(c => /^(target|to|dest|destination|sink)$/.test(c)) ?? lowerCols.findIndex(c => c.includes("target") || c.includes("dest")) ?? 1;
  const valueIdx = lowerCols.findIndex(c => /^(value|count|amount|total|flow|weight|quantity|n)$/.test(c)) ?? lowerCols.findIndex(c => c.includes("count") || c.includes("value")) ?? 2;
  const effectiveSourceIdx = sourceIdx < 0 ? 0 : sourceIdx;
  const effectiveTargetIdx = targetIdx < 0 ? 1 : targetIdx;
  const effectiveValueIdx = valueIdx < 0 ? 2 : valueIdx;

  // Build unique node list (preserve insertion order for stable indices)
  const nodeNames: string[] = [];
  const nodeIndex = new Map<string, number>();
  const addNode = (name: string) => {
    if (!nodeIndex.has(name)) {
      nodeIndex.set(name, nodeNames.length);
      nodeNames.push(name);
    }
  };
  for (const row of rows) {
    addNode(String(row[effectiveSourceIdx] ?? ""));
    addNode(String(row[effectiveTargetIdx] ?? ""));
  }
  const links = rows.map(row => ({
    source: nodeIndex.get(String(row[effectiveSourceIdx] ?? "")) ?? 0,
    target: nodeIndex.get(String(row[effectiveTargetIdx] ?? "")) ?? 0,
    value: Math.max(0.001, typeof row[effectiveValueIdx] === "number" ? row[effectiveValueIdx] as number : Number(row[effectiveValueIdx]) || 0)
  })).filter(l => l.source !== l.target);
  return {
    nodes: nodeNames.map(name => ({
      name
    })),
    links
  };
}

/** Export table data as CSV download */
function exportTableAsCSV(columns: string[], rows: unknown[][], filename = "analytics-export.csv") {
  const escapeCSV = (val: unknown) => {
    const str = val === null || val === undefined ? "" : String(val);
    if (str.includes(",") || str.includes('"') || str.includes("\n")) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };
  const header = columns.map(escapeCSV).join(",");
  const body = rows.map(row => row.map(escapeCSV).join(",")).join("\n");
  const csv = `${header}\n${body}`;
  const blob = new Blob([csv], {
    type: "text/csv;charset=utf-8;"
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Small footer action button used in chart footers */
function FooterButton(t0) {
  const $ = _c(9);
  const {
    icon: Icon,
    label,
    onClick,
    title
  } = t0;
  let t1;
  if ($[0] !== Icon) {
    t1 = <Icon className="h-3 w-3" />;
    $[0] = Icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label) {
    t2 = <span>{label}</span>;
    $[2] = label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== onClick || $[5] !== t1 || $[6] !== t2 || $[7] !== title) {
    t3 = <button type="button" onClick={onClick} className="flex items-center gap-1 rounded px-1.5 py-0.5 transition-colors hover:bg-hover hover:text-foreground" title={title}>{t1}{t2}</button>;
    $[4] = onClick;
    $[5] = t1;
    $[6] = t2;
    $[7] = title;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}

/** Shared wrapper for pie/line/bar/scatter chart sections */
function ChartContainer(t0) {
  const $ = _c(26);
  const {
    chartTitle,
    message,
    footer,
    hasData,
    chartRef,
    autoHeight,
    children
  } = t0;
  const [collapsed, setCollapsed] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setCollapsed(_temp);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = collapsed ? "Expand chart" : "Collapse chart";
  const t3 = !collapsed && "rotate-90";
  let t4;
  if ($[1] !== t3) {
    t4 = cx("h-3.5 w-3.5 transition-transform duration-200", t3);
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== t4) {
    t5 = <FiChevronRight className={t4} />;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== t2 || $[6] !== t5) {
    t6 = <button type="button" onClick={t1} className="flex items-center gap-1 rounded p-0.5 text-foreground-secondary hover:bg-hover hover:text-foreground transition-colors" aria-label={t2}>{t5}</button>;
    $[5] = t2;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== chartTitle) {
    t7 = chartTitle && <h4 className="text-body-strong text-foreground">{chartTitle}</h4>;
    $[8] = chartTitle;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t6 || $[11] !== t7) {
    t8 = <div className="flex items-center gap-1">{t6}{t7}</div>;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== message) {
    t9 = message && <p className="text-caption text-foreground-muted">{message}</p>;
    $[13] = message;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== autoHeight || $[16] !== chartRef || $[17] !== children || $[18] !== collapsed || $[19] !== footer || $[20] !== hasData) {
    t10 = !collapsed && <><div ref={chartRef} className={autoHeight ? "w-full" : "h-80 w-full pb-6"}><ChartWrapper isDataLoaded={hasData} isLoading={false}>{children}</ChartWrapper></div>{footer}</>;
    $[15] = autoHeight;
    $[16] = chartRef;
    $[17] = children;
    $[18] = collapsed;
    $[19] = footer;
    $[20] = hasData;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t8 || $[24] !== t9) {
    t11 = <div className="space-y-2 pb-4">{t8}{t9}{t10}</div>;
    $[22] = t10;
    $[23] = t8;
    $[24] = t9;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  return t11;
}
function _temp(c) {
  return !c;
}
interface AnalyticsArtifactRendererProps {
  result: AnalyticsResult;
  /** Trusted pin interaction, placed alongside the existing card exports. */
  pinAction?: React.ReactNode;
}

/** Renders a single artifact (chart, table, or text value) */
export function AnalyticsArtifactRenderer({
  result,
  pinAction
}: AnalyticsArtifactRendererProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  // Hooks must always run — declare before any conditional returns
  const handleExportPng = useCallback(() => {
    const title = result.chart_title || "chart";
    const filename = title.toLowerCase().replace(/[^a-z0-9]+/g, "-") + ".png";
    void exportChartAsPng(chartRef.current, filename);
  }, [result.chart_title]);

  // This is the backend-selected card list, including historical untyped cards.
  if (result.artifacts?.length) {
    return <div className="space-y-4">
        {result.artifacts.map((artifact, i) => <AnalyticsArtifactRenderer key={i} result={{
        ...artifact,
        explanation: artifact.explanation ?? "",
        result_type: AnalyticsResultType.TABLE,
        message: null,
        sql: artifact.sql ?? null,
        execution_time_ms: artifact.execution_time_ms ?? 0
      }} pinAction={pinAction} />)}
      </div>;
  }

  // Untyped historical results remain tables; only the agent selects a chart.
  const effectiveType = result.chart_type ?? AnalyticsChartType.TABLE;
  const columnTypes = result.column_types;

  // Message/question: no artifact beyond the message content (handled by parent)
  if (result.result_type === AnalyticsResultType.MESSAGE || result.result_type === AnalyticsResultType.QUESTION) {
    return null;
  }
  if (result.rows.length === 0) {
    const label = result.explanation || "No data found for the selected period.";
    return <p className="text-body text-foreground-secondary">{label}</p>;
  }

  // Agent-selected metric — normally the chat bubble narrative duplicates this.
  // When there is no narrative show plain text.
  if (effectiveType === AnalyticsChartType.METRIC) {
    if (result.narrative?.trim()) {
      return pinAction ? <>{pinAction}</> : null;
    }
    return <div className="space-y-1.5 text-body text-foreground">
        {result.chart_title && <h4 className="text-body-strong">{result.chart_title}</h4>}
        {result.rows.map((row, rowIdx) => <p key={rowIdx}>
            {result.columns.map((col, colIdx) => <span key={`${col}-${colIdx}`}>
                <span>{colIdx > 0 ? ", " : null}</span>
                <span className="text-foreground-secondary">
                  {humanizeColumnName(col)}:{" "}
                </span>
                {formatCell(row[colIdx], colIdx, col, columnTypes, result.currency_column_indices, result.currency_columns)}
              </span>)}
          </p>)}
        {pinAction}
      </div>;
  }

  // When the agent specifies column mapping, use it directly (data is already wide format).
  // Otherwise fall back to auto-pivot for long-format data.
  const hasColumnMapping = !!result.x_column || (result.y_columns?.length ?? 0) > 0;
  const chartResult = effectiveType === AnalyticsChartType.TABLE ? result : hasColumnMapping ? result : autoPivotForChart(result);

  // Validate agent-specified x_column/y_columns against actual columns — Python post-processing
  // renames columns to human-readable labels, making the original names stale.
  const xColRaw = result.x_column && chartResult.columns.includes(result.x_column) ? result.x_column : chartResult.columns[0] ?? "name";
  const indexKey = humanizeColumnName(xColRaw);
  const validYCols = (result.y_columns ?? []).filter(c => chartResult.columns.includes(c));
  const rawValueCols = validYCols.length > 0 ? validYCols : chartResult.columns.filter(c_0 => c_0 !== xColRaw).slice(0, 8);
  const candidateValueKeys = rawValueCols.length > 0 ? rawValueCols : chartResult.columns.slice(1);
  const valueKeys = candidateValueKeys.map(humanizeColumnName);
  const chartData = effectiveType === AnalyticsChartType.TABLE ? [] : rowsToChartData(chartResult, xColRaw, candidateValueKeys);

  // Only use currency formatting when ALL plotted value columns are currency.
  // Mixed series (e.g. revenue + MoM%) must use plain number formatting to avoid
  // showing percentage values like 266 as "$270.00".
  const valueCols = rawValueCols.length > 0 ? rawValueCols : chartResult.columns.slice(1);
  const allValueColsAreCurrency = valueCols.length > 0 && valueCols.every(col_0 => result.currency_columns ? result.currency_columns.includes(col_0) : result.currency_column_indices ? result.currency_column_indices.includes(result.columns.indexOf(col_0)) : columnTypes?.[col_0] === "currency");
  const chartValueFormatter = (v: number): string => allValueColsAreCurrency ? formatCurrencyDollars(v) : v.toLocaleString();
  const chartTitle = result.chart_title;
  // Auto-generate axis labels from column names when the agent doesn't provide them
  const xLabel = result.x_label || humanizeColumnName(xColRaw);
  const yLabel = result.y_label || (valueKeys.length === 1 ? valueKeys[0] : null) || null;
  const handleExportCSV = () => {
    exportTableAsCSV(result.columns, result.rows);
  };
  const footerButtons: {
    icon: React.ComponentType<{
      className?: string;
    }>;
    label: string;
    onClick: () => void;
    title: string;
  }[] = [{
    icon: FiImage,
    label: "PNG",
    onClick: handleExportPng,
    title: "Save as image"
  }, {
    icon: FiDownload,
    label: "CSV",
    onClick: handleExportCSV,
    title: "Export data as CSV"
  }];
  const chartFooter = (label_0: string) => <div className="mt-2 flex items-center justify-between text-caption text-foreground-muted">
      <span>
        {(result.execution_time_ms / 1000).toFixed(1)}s · {label_0}
      </span>
      <div className="flex items-center gap-1">
        {pinAction}
        {footerButtons.map(btn => <FooterButton key={btn.label} icon={btn.icon} label={btn.label} onClick={btn.onClick} title={btn.title} />)}
      </div>
    </div>;

  // If every value in every value column is null/undefined, show a message instead of blank chart
  const hasAnyChartData = effectiveType === AnalyticsChartType.TABLE || chartData.some(row_0 => valueKeys.some(k => row_0[k] !== null && row_0[k] !== undefined));
  if (!hasAnyChartData) {
    return <p className="text-body text-foreground-secondary">
        {result.explanation || "No numeric data available to chart."}
      </p>;
  }

  // Pie/Donut: category (first col) + value (second col)
  if (effectiveType === AnalyticsChartType.PIE) {
    return <ChartContainer chartTitle={chartTitle} message={result.message} footer={chartFooter(`${result.rows.length} categories`)} hasData={chartData.length > 0} chartRef={chartRef}>
        <DonutChart data={chartData} category={indexKey} value={valueKeys[0] ?? "value"} valueFormatter={chartValueFormatter} showLegend className="size-full" />
      </ChartContainer>;
  }

  // Line/Area
  if (effectiveType === AnalyticsChartType.LINE) {
    const hasNegativeValues = chartData.some(row_1 => valueKeys.some(k_0 => typeof row_1[k_0] === "number" && row_1[k_0] as number < 0));
    return <ChartContainer chartTitle={chartTitle} message={result.message} footer={chartFooter(`${result.rows.length} points`)} hasData={chartData.length > 0} chartRef={chartRef}>
        <AreaChart data={chartData} index={indexKey} categories={valueKeys} valueFormatter={chartValueFormatter} xAxisLabel={xLabel ?? undefined} yAxisLabel={yLabel ?? undefined} autoMinValue={hasNegativeValues} />
      </ChartContainer>;
  }

  // Bar
  if (effectiveType === AnalyticsChartType.BAR) {
    const hasNegativeValues_0 = chartData.some(row_2 => valueKeys.some(k_1 => typeof row_2[k_1] === "number" && row_2[k_1] as number < 0));
    return <ChartContainer chartTitle={chartTitle} message={result.message} footer={chartFooter(`${result.rows.length} rows`)} hasData={chartData.length > 0} chartRef={chartRef}>
        <BarChart data={chartData} index={indexKey} categories={valueKeys} valueFormatter={chartValueFormatter} xAxisLabel={xLabel ?? undefined} yAxisLabel={yLabel ?? undefined} autoMinValue={hasNegativeValues_0} />
      </ChartContainer>;
  }

  // Scatter
  if (effectiveType === AnalyticsChartType.SCATTER) {
    const scatterData = rowsToScatterData(result);

    // Date columns always get a date formatter — never currency or plain number
    const dateFormatter = (v_0: number) => formatDate(new Date(v_0));

    // Build per-axis formatters: dates first, then currency (agent-specified or inferred), then plain number
    const currencySet = result.currency_columns ? new Set(result.currency_columns) : undefined;
    const isXCurrency = !scatterData.xIsDate && (currencySet ? currencySet.has(scatterData.xColumn) : columnTypes?.[scatterData.xColumn] === "currency");
    const isYCurrency = !scatterData.yIsDate && (currencySet ? currencySet.has(scatterData.yColumn) : columnTypes?.[scatterData.yColumn] === "currency");
    const scatterXFormatter = scatterData.xIsDate ? dateFormatter : isXCurrency ? formatCurrencyDollars : (v_1: number) => v_1.toLocaleString();
    const scatterYFormatter = scatterData.yIsDate ? dateFormatter : isYCurrency ? formatCurrencyDollars : (v_2: number) => v_2.toLocaleString();
    return <ChartContainer chartTitle={chartTitle} message={result.message} footer={chartFooter(`${result.rows.length} points`)} hasData={scatterData.points.length > 0} chartRef={chartRef}>
        <ScatterChart data={scatterData.points} xAxisLabel={xLabel ?? scatterData.xLabel} yAxisLabel={yLabel ?? scatterData.yLabel} xValueFormatter={scatterXFormatter} yValueFormatter={scatterYFormatter} showDensity={scatterData.points.length > 20} autoMinValue />
      </ChartContainer>;
  }

  // Area (dedicated area chart — same component as "line" but always filled)
  if (effectiveType === AnalyticsChartType.AREA) {
    const hasNegativeValues_1 = chartData.some(row_3 => valueKeys.some(k_2 => typeof row_3[k_2] === "number" && row_3[k_2] as number < 0));
    return <ChartContainer chartTitle={chartTitle} message={result.message} footer={chartFooter(`${result.rows.length} points`)} hasData={chartData.length > 0} chartRef={chartRef}>
        <AreaChart data={chartData} index={indexKey} categories={valueKeys} valueFormatter={chartValueFormatter} xAxisLabel={xLabel ?? undefined} yAxisLabel={yLabel ?? undefined} autoMinValue={hasNegativeValues_1} />
      </ChartContainer>;
  }

  // ComposedChart — bars (left axis) + lines (right axis). Columns matching
  // rate/% pattern go to lines; otherwise last column is line, rest are bars.
  if (effectiveType === AnalyticsChartType.COMPOSED) {
    const ratePattern = /rate|percent|%|change|growth|ratio|pct/i;
    const lineKeys = valueKeys.filter(k_3 => ratePattern.test(k_3));
    let effectiveBarKeys: string[];
    let effectiveLineKeys: string[];
    if (lineKeys.length > 0) {
      effectiveLineKeys = lineKeys;
      effectiveBarKeys = valueKeys.filter(k_4 => !lineKeys.includes(k_4));
    } else if (valueKeys.length > 1) {
      effectiveBarKeys = valueKeys.slice(0, -1);
      effectiveLineKeys = valueKeys.slice(-1);
    } else {
      effectiveBarKeys = valueKeys;
      effectiveLineKeys = [];
    }
    return <ChartContainer chartTitle={chartTitle} message={result.message} footer={chartFooter(`${result.rows.length} rows`)} hasData={chartData.length > 0} chartRef={chartRef}>
        <ComposedChart data={chartData} index={indexKey} barCategories={effectiveBarKeys} lineCategories={effectiveLineKeys} valueFormatter={chartValueFormatter} xAxisLabel={xLabel ?? undefined} yAxisLabel={yLabel ?? undefined} />
      </ChartContainer>;
  }

  // Biaxial line chart — two Y axes for series on very different scales
  if (effectiveType === AnalyticsChartType.BIAXIAL) {
    return <ChartContainer chartTitle={chartTitle} message={result.message} footer={chartFooter(`${result.rows.length} points`)} hasData={chartData.length > 0} chartRef={chartRef}>
        <BiaxialLineChart data={chartData} index={indexKey} leftCategories={valueKeys.slice(0, Math.ceil(valueKeys.length / 2))} rightCategories={valueKeys.slice(Math.ceil(valueKeys.length / 2))} leftFormatter={chartValueFormatter} rightFormatter={v_3 => v_3.toLocaleString()} xAxisLabel={xLabel ?? undefined} />
      </ChartContainer>;
  }

  // Stacked bar by sign (positive values stack up, negative stack down)
  if (effectiveType === AnalyticsChartType.STACKED_SIGN) {
    return <ChartContainer chartTitle={chartTitle} message={result.message} footer={chartFooter(`${result.rows.length} rows`)} hasData={chartData.length > 0} chartRef={chartRef}>
        <BarChart data={chartData} index={indexKey} categories={valueKeys} valueFormatter={chartValueFormatter} xAxisLabel={xLabel ?? undefined} yAxisLabel={yLabel ?? undefined} type="sign" autoMinValue />
      </ChartContainer>;
  }

  // Funnel / conversion chart
  if (effectiveType === AnalyticsChartType.FUNNEL) {
    return <ChartContainer chartTitle={chartTitle} message={result.message} footer={chartFooter(`${result.rows.length} stages`)} hasData={chartData.length > 0} chartRef={chartRef}>
        <FunnelChart data={chartData} index={indexKey} category={valueKeys[0] ?? "value"} valueFormatter={chartValueFormatter} className="size-full" />
      </ChartContainer>;
  }

  // Sankey flow diagram
  if (effectiveType === AnalyticsChartType.SANKEY) {
    const sankeyData = rowsToSankeyData(result);
    return <ChartContainer chartTitle={chartTitle} message={result.message} footer={chartFooter(`${sankeyData.links.length} flows`)} hasData={sankeyData.links.length > 0} chartRef={chartRef} autoHeight>
        <SankeyChart data={sankeyData} valueFormatter={chartValueFormatter} />
      </ChartContainer>;
  }

  // Treemap — proportional area blocks for part-to-whole with many categories
  if (effectiveType === AnalyticsChartType.TREEMAP) {
    const treemapIndexKey = chartResult.columns[0] ?? "name";
    const treemapCategoryKey = chartResult.columns.find((c_1, i_0) => i_0 > 0 && chartData.some(row_4 => typeof row_4[humanizeColumnName(c_1)] === "number" || !Number.isNaN(Number(row_4[humanizeColumnName(c_1)])) && String(row_4[humanizeColumnName(c_1)]).trim() !== ""));
    const treemapIndex = humanizeColumnName(treemapIndexKey);
    const treemapCategory = humanizeColumnName(treemapCategoryKey ?? chartResult.columns[1] ?? "value");
    return <ChartContainer chartTitle={chartTitle} message={result.message} footer={chartFooter(`${result.rows.length} categories`)} hasData={chartData.length > 0} chartRef={chartRef}>
        <TreemapChart data={chartData} index={treemapIndex} category={treemapCategory} valueFormatter={chartValueFormatter} className="size-full" />
      </ChartContainer>;
  }

  // Box plot — distribution summary (min/Q1/median/Q3/max per category)
  if (effectiveType === AnalyticsChartType.BOX_PLOT) {
    const boxData: BoxPlotEntry[] = result.rows.map(row_5 => ({
      name: String(row_5[0]),
      min: Number(row_5[1]),
      q1: Number(row_5[2]),
      median: Number(row_5[3]),
      q3: Number(row_5[4]),
      max: Number(row_5[5])
    }));
    return <ChartContainer chartTitle={chartTitle} message={result.message} footer={chartFooter(`${result.rows.length} categories`)} hasData={boxData.length > 0} chartRef={chartRef}>
        <BoxPlotChart data={boxData} valueFormatter={chartValueFormatter} className="size-full" />
      </ChartContainer>;
  }

  // Table (default)
  return <CollapsibleTable result={result} columnTypes={columnTypes} pinAction={pinAction} />;
}
function TableActions(t0) {
  const $ = _c(9);
  const {
    result,
    pinAction
  } = t0;
  let t1;
  if ($[0] !== result.columns || $[1] !== result.rows) {
    t1 = event => {
      event.stopPropagation();
      exportTableAsCSV(result.columns, result.rows);
    };
    $[0] = result.columns;
    $[1] = result.rows;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <FiDownload className="h-3.5 w-3.5" />;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1) {
    t3 = <button type="button" onClick={t1} className="rounded p-1.5 text-foreground-muted transition-colors hover:bg-hover hover:text-foreground" aria-label="Export as CSV">{t2}</button>;
    $[4] = t1;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== pinAction || $[7] !== t3) {
    t4 = <div className="flex items-center gap-1">{pinAction}{t3}</div>;
    $[6] = pinAction;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
const AGENT_TABLE_PAGE_SIZE = 15;
const tableValueCollator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base"
});

/** Sort source values, not formatted labels, keeping empty cells last. */
function compareTableValues(left: unknown, right: unknown): number {
  if (left == null) return right == null ? 0 : 1;
  if (right == null) return -1;
  if (typeof left === "number" && typeof right === "number") {
    return left - right;
  }
  // SQL decimals may be strings to retain precision. Compare decimal digits
  // directly so negative values and large integers sort without rounding.
  const decimalPattern = /^([+-]?)(\d+)(?:\.(\d+))?$/;
  const a = decimalPattern.exec(String(left));
  const b = decimalPattern.exec(String(right));
  if (a && b) {
    const aInteger = a[2].replace(/^0+/, "") || "0";
    const bInteger = b[2].replace(/^0+/, "") || "0";
    const aFraction = a[3] ?? "";
    const bFraction = b[3] ?? "";
    const aNegative = a[1] === "-" && /[1-9]/.test(aInteger + aFraction);
    const bNegative = b[1] === "-" && /[1-9]/.test(bInteger + bFraction);
    if (aNegative !== bNegative) return aNegative ? -1 : 1;
    const fractionLength = Math.max(aFraction.length, bFraction.length);
    const comparison = aInteger.length - bInteger.length || aInteger.localeCompare(bInteger) || aFraction.padEnd(fractionLength, "0").localeCompare(bFraction.padEnd(fractionLength, "0"));
    return aNegative ? -comparison : comparison;
  }
  return tableValueCollator.compare(String(left), String(right));
}

/** Collapsible table with client-side sorting and pagination. */
function CollapsibleTable(t0) {
  const $ = _c(91);
  const {
    result,
    columnTypes,
    pinAction
  } = t0;
  const [tableOpen, setTableOpen] = useState(true);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState(null);
  const rowCount = result.rows.length;
  const currentPage = Math.min(page, Math.max(1, Math.ceil(rowCount / AGENT_TABLE_PAGE_SIZE)));
  let t1;
  bb0: {
    if (!sort || sort.column >= result.columns.length) {
      t1 = result.rows;
      break bb0;
    }
    const {
      column,
      direction
    } = sort;
    let t2;
    if ($[0] !== column || $[1] !== direction || $[2] !== result.rows) {
      let t3;
      if ($[4] !== column || $[5] !== direction) {
        t3 = (left, right) => {
          if (left[column] == null || right[column] == null) {
            return compareTableValues(left[column], right[column]);
          }
          const comparison = compareTableValues(left[column], right[column]);
          return direction === "ascending" ? comparison : -comparison;
        };
        $[4] = column;
        $[5] = direction;
        $[6] = t3;
      } else {
        t3 = $[6];
      }
      t2 = [...result.rows].sort(t3);
      $[0] = column;
      $[1] = direction;
      $[2] = result.rows;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    t1 = t2;
  }
  const sortedRows = t1;
  let T0;
  let T1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[7] !== columnTypes || $[8] !== currentPage || $[9] !== pinAction || $[10] !== result || $[11] !== rowCount || $[12] !== sort || $[13] !== sortedRows || $[14] !== tableOpen) {
    const visibleRows = sortedRows.slice((currentPage - 1) * AGENT_TABLE_PAGE_SIZE, currentPage * AGENT_TABLE_PAGE_SIZE);
    T1 = Collapsible;
    t6 = tableOpen;
    t7 = setTableOpen;
    const t9 = tableOpen && "rounded-b-none";
    let t10;
    if ($[24] !== t9) {
      t10 = cx("flex flex-col rounded-lg border border-border", t9);
      $[24] = t9;
      $[25] = t10;
    } else {
      t10 = $[25];
    }
    let t11;
    if ($[26] !== pinAction || $[27] !== result) {
      t11 = result.chart_title && <div className="flex items-center justify-between border-b border-border px-3 py-2"><h4 className="text-body-strong text-foreground">{result.chart_title}</h4><TableActions result={result} pinAction={pinAction} /></div>;
      $[26] = pinAction;
      $[27] = result;
      $[28] = t11;
    } else {
      t11 = $[28];
    }
    const t12 = tableOpen && "rotate-90";
    let t13;
    if ($[29] !== t12) {
      t13 = cx("h-4 w-4 shrink-0 transition-transform", t12);
      $[29] = t12;
      $[30] = t13;
    } else {
      t13 = $[30];
    }
    let t14;
    if ($[31] !== t13) {
      t14 = <FiChevronRight className={t13} />;
      $[31] = t13;
      $[32] = t14;
    } else {
      t14 = $[32];
    }
    let t15;
    if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <FiList className="h-4 w-4 shrink-0 text-foreground-muted" />;
      $[33] = t15;
    } else {
      t15 = $[33];
    }
    const t16 = rowCount !== 1 ? "s" : "";
    let t17;
    if ($[34] !== t16) {
      t17 = <span>{t16}</span>;
      $[34] = t16;
      $[35] = t17;
    } else {
      t17 = $[35];
    }
    let t18;
    if ($[36] !== rowCount || $[37] !== t17) {
      t18 = <span className="font-medium">{rowCount} row{t17}</span>;
      $[36] = rowCount;
      $[37] = t17;
      $[38] = t18;
    } else {
      t18 = $[38];
    }
    const t19 = result.execution_time_ms / 1000;
    let t20;
    if ($[39] !== t19) {
      t20 = t19.toFixed(1);
      $[39] = t19;
      $[40] = t20;
    } else {
      t20 = $[40];
    }
    let t21;
    if ($[41] !== t20) {
      t21 = <span className="text-caption text-foreground-muted">{t20}s</span>;
      $[41] = t20;
      $[42] = t21;
    } else {
      t21 = $[42];
    }
    let t22;
    if ($[43] !== t14 || $[44] !== t18 || $[45] !== t21) {
      t22 = <CollapsibleTrigger className="flex flex-1 items-center gap-2 px-3 py-2 text-left text-body text-foreground hover:bg-hover">{t14}{t15}{t18}{t21}</CollapsibleTrigger>;
      $[43] = t14;
      $[44] = t18;
      $[45] = t21;
      $[46] = t22;
    } else {
      t22 = $[46];
    }
    let t23;
    if ($[47] !== pinAction || $[48] !== result) {
      t23 = !result.chart_title && <div className="mr-2"><TableActions result={result} pinAction={pinAction} /></div>;
      $[47] = pinAction;
      $[48] = result;
      $[49] = t23;
    } else {
      t23 = $[49];
    }
    let t24;
    if ($[50] !== t22 || $[51] !== t23) {
      t24 = <div className="flex items-center justify-between">{t22}{t23}</div>;
      $[50] = t22;
      $[51] = t23;
      $[52] = t24;
    } else {
      t24 = $[52];
    }
    if ($[53] !== t10 || $[54] !== t11 || $[55] !== t24) {
      t8 = <div className={t10}>{t11}{t24}</div>;
      $[53] = t10;
      $[54] = t11;
      $[55] = t24;
      $[56] = t8;
    } else {
      t8 = $[56];
    }
    T0 = CollapsibleContent;
    t5 = "overflow-auto rounded-b-lg border border-t-0 border-border";
    t3 = "w-full text-body";
    let t25;
    if ($[57] !== result.columns || $[58] !== sort) {
      let t26;
      if ($[60] !== sort) {
        t26 = (col, i) => <th key={i} scope="col" aria-sort={sort?.column === i ? sort.direction : "none"} className="text-left font-medium text-table-header-foreground"><button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-hover" onClick={() => {
            setSort({
              column: i,
              direction: sort?.column === i && sort.direction === "ascending" ? "descending" : "ascending"
            });
            setPage(1);
          }}>{col}{sort?.column === i && sort.direction === "descending" ? <FiArrowDown aria-hidden="true" className="h-3.5 w-3.5 shrink-0" /> : <FiArrowUp aria-hidden="true" className={cx("h-3.5 w-3.5 shrink-0", sort?.column !== i && "text-foreground-muted")} />}</button></th>;
        $[60] = sort;
        $[61] = t26;
      } else {
        t26 = $[61];
      }
      t25 = result.columns.map(t26);
      $[57] = result.columns;
      $[58] = sort;
      $[59] = t25;
    } else {
      t25 = $[59];
    }
    if ($[62] !== t25) {
      t4 = <thead><tr className="border-b border-border bg-table-header">{t25}</tr></thead>;
      $[62] = t25;
      $[63] = t4;
    } else {
      t4 = $[63];
    }
    let t26;
    if ($[64] !== columnTypes || $[65] !== result.columns || $[66] !== result.currency_column_indices || $[67] !== result.currency_columns) {
      t26 = (row, rowIdx) => <tr key={rowIdx} className={cx("border-b border-table-border last:border-0", "hover:bg-row-hover")}>{row.map((cell, cellIdx) => <td key={cellIdx} className="px-3 py-2 text-foreground tabular-nums">{formatCell(cell, cellIdx, result.columns[cellIdx] ?? "", columnTypes, result.currency_column_indices, result.currency_columns)}</td>)}</tr>;
      $[64] = columnTypes;
      $[65] = result.columns;
      $[66] = result.currency_column_indices;
      $[67] = result.currency_columns;
      $[68] = t26;
    } else {
      t26 = $[68];
    }
    t2 = visibleRows.map(t26);
    $[7] = columnTypes;
    $[8] = currentPage;
    $[9] = pinAction;
    $[10] = result;
    $[11] = rowCount;
    $[12] = sort;
    $[13] = sortedRows;
    $[14] = tableOpen;
    $[15] = T0;
    $[16] = T1;
    $[17] = t2;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
  } else {
    T0 = $[15];
    T1 = $[16];
    t2 = $[17];
    t3 = $[18];
    t4 = $[19];
    t5 = $[20];
    t6 = $[21];
    t7 = $[22];
    t8 = $[23];
  }
  let t9;
  if ($[69] !== t2) {
    t9 = <tbody>{t2}</tbody>;
    $[69] = t2;
    $[70] = t9;
  } else {
    t9 = $[70];
  }
  let t10;
  if ($[71] !== t3 || $[72] !== t4 || $[73] !== t9) {
    t10 = <table className={t3}>{t4}{t9}</table>;
    $[71] = t3;
    $[72] = t4;
    $[73] = t9;
    $[74] = t10;
  } else {
    t10 = $[74];
  }
  let t11;
  if ($[75] !== currentPage || $[76] !== rowCount) {
    t11 = rowCount > AGENT_TABLE_PAGE_SIZE && <Pagination displayText="rows" total={rowCount} currentPage={currentPage} perPage={AGENT_TABLE_PAGE_SIZE} handlePageChange={direction_0 => setPage(currentPage + (direction_0 === "next" ? 1 : -1))} />;
    $[75] = currentPage;
    $[76] = rowCount;
    $[77] = t11;
  } else {
    t11 = $[77];
  }
  let t12;
  if ($[78] !== t10 || $[79] !== t11 || $[80] !== t5) {
    t12 = <div className={t5}>{t10}{t11}</div>;
    $[78] = t10;
    $[79] = t11;
    $[80] = t5;
    $[81] = t12;
  } else {
    t12 = $[81];
  }
  let t13;
  if ($[82] !== T0 || $[83] !== t12) {
    t13 = <T0>{t12}</T0>;
    $[82] = T0;
    $[83] = t12;
    $[84] = t13;
  } else {
    t13 = $[84];
  }
  let t14;
  if ($[85] !== T1 || $[86] !== t13 || $[87] !== t6 || $[88] !== t7 || $[89] !== t8) {
    t14 = <T1 open={t6} onOpenChange={t7}>{t8}{t13}</T1>;
    $[85] = T1;
    $[86] = t13;
    $[87] = t6;
    $[88] = t7;
    $[89] = t8;
    $[90] = t14;
  } else {
    t14 = $[90];
  }
  return t14;
}

// ---------------------------------------------------------------------------
// Mode 2: Inline chat artifact renderer
// ---------------------------------------------------------------------------

/** Render a metric as a big number card */
function MetricCard(t0) {
  const $ = _c(11);
  const {
    value,
    label,
    isCurrency
  } = t0;
  let formatted;
  if (isCurrency && (typeof value === "number" || typeof value === "string")) {
    let t1;
    if ($[0] !== value) {
      t1 = formatCurrencyDollars(value);
      $[0] = value;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    formatted = t1;
  } else {
    if (typeof value === "number") {
      let t1;
      if ($[2] !== value) {
        t1 = value.toLocaleString();
        $[2] = value;
        $[3] = t1;
      } else {
        t1 = $[3];
      }
      formatted = t1;
    } else {
      formatted = String(value ?? "");
    }
  }
  let t1;
  if ($[4] !== label) {
    t1 = <div className="text-overline text-foreground-secondary">{label}</div>;
    $[4] = label;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== formatted) {
    t2 = <div className="mt-1 text-display tabular-nums text-foreground">{formatted}</div>;
    $[6] = formatted;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== t1 || $[9] !== t2) {
    t3 = <div className="rounded-lg border border-border bg-background-secondary px-4 py-4">{t1}{t2}</div>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
}

/**
 * Convert a Mode 2 ChatArtifact into an AnalyticsResult so that the
 * existing AnalyticsArtifactRenderer can render it. The chartConfig fields
 * are mapped onto the AnalyticsResult shape.
 */
function chatArtifactToResult(artifact: ChatArtifact, chartConfig?: ChatArtifactChartConfig): AnalyticsResult {
  // Map Mode 2 chartType to the existing chart_type hint
  const CHART_TYPE_MAP: Record<string, AnalyticsResult["chart_type"]> = {
    line: AnalyticsChartType.LINE,
    bar: AnalyticsChartType.BAR,
    donut: AnalyticsChartType.PIE,
    scatter: AnalyticsChartType.SCATTER,
    table: AnalyticsChartType.TABLE,
    metric: AnalyticsChartType.METRIC
  };
  const chartType = chartConfig ? CHART_TYPE_MAP[chartConfig.chartType] : undefined;
  return {
    explanation: artifact.explanation ?? "",
    result_type: AnalyticsResultType.TABLE,
    columns: artifact.columns ?? [],
    rows: artifact.rows ?? [],
    message: null,
    sql: artifact.sql ?? null,
    execution_time_ms: 0,
    column_types: artifact.column_types,
    python_code: artifact.python_code,
    chart_type: chartType ?? artifact.chart_type as AnalyticsResult["chart_type"],
    x_label: chartConfig?.xLabel,
    y_label: chartConfig?.yLabel,
    x_column: chartConfig?.xColumn,
    y_columns: chartConfig?.yColumns
  };
}
interface ChatArtifactRendererProps {
  artifact: ChatArtifact;
}

/**
 * Renders a single inline chat artifact.
 *
 * Handles the Mode 2 `chartConfig` shape:
 * - metric -> MetricCard
 * - bar -> BarChart
 * - line -> AreaChart
 * - donut -> DonutChart
 * - table -> CollapsibleTable
 * - scatter -> ScatterChart
 *
 * Falls back to the existing AnalyticsArtifactRenderer for standard rendering.
 */
export function ChatArtifactRenderer(t0) {
  const $ = _c(9);
  const {
    artifact
  } = t0;
  const config = artifact.chartConfig;
  const chartType = config?.chartType ?? artifact.chart_type;
  if (chartType === "metric") {
    const value = artifact.rows?.[0]?.[0];
    const label = artifact.columns?.[0] ?? "";
    const isCurrency = artifact.column_types?.[label] === "currency";
    let t1;
    if ($[0] !== isCurrency || $[1] !== label || $[2] !== value) {
      t1 = <MetricCard value={value} label={label} isCurrency={isCurrency} />;
      $[0] = isCurrency;
      $[1] = label;
      $[2] = value;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    return t1;
  }
  let t1;
  if ($[4] !== artifact || $[5] !== config) {
    t1 = chatArtifactToResult(artifact, config);
    $[4] = artifact;
    $[5] = config;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const result = t1;
  if (!result.rows || result.rows.length === 0) {
    return null;
  }
  let t2;
  if ($[7] !== result) {
    t2 = <AnalyticsArtifactRenderer result={result} />;
    $[7] = result;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}